import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Chart from 'react-apexcharts';
import qs from 'query-string';

import { useGetDistributorSubscribersDataQuery, useGetInvestmentFundsQuery } from 'api/main';
import { texts } from 'utils/languages/fr';
import { queryStringifyOptions, subscriptionStatus } from 'utils/constants';
import { parseFullName, parseSum } from 'utils/helpers';

import { ChartContainer, DashboardSection } from '../common-components';
import { CustomSelect, Loader } from 'components';
import { Typography, Box, MenuItem } from '@mui/material';
import {
    customTotalParser,
    parseCustomData,
    pieChartOptions,
    subscriptionNumberFormatter,
} from './AdminDistributorDashboard';

const getChartSubtitle = (data, filter, category) => {
    const type = data === 'all' ? 'PRODUCT' : 'SHARE';
    switch (filter) {
        case 'all':
            return texts[`SUBSCRIPTIONS_${category}_BY_${type}`];
        case 'transferred':
            return texts[`TRANSFERED_SUBSCRIPTIONS_${category}_BY_${type}`];
        case 'pending':
            return texts[`NON_TRANSFERED_SUBSCRIPTIONS_${category}_BY_${type}`];
        default:
            return texts[`SUBSCRIPTIONS_${category}_BY_${type}`];
    }
};

const getSumKeyForFilter = filter => {
    switch (filter) {
        case 'all':
            return 'sum_total_subscriptions';
        case 'transferred':
            return 'sum_subscriptions_transferred';
        case 'pending':
            return 'sum_subscriptions_pending_transfer';
        default:
            return 'sum_total_subscriptions';
    }
};

const getNumberKeyForFilter = filter => {
    switch (filter) {
        case 'all':
            return 'total_subscriptions';
        case 'transferred':
            return 'subscriptions_transferred';
        case 'pending':
            return 'subscriptions_pending_transfer';
        default:
            return 'total_subscriptions';
    }
};

const getNoDataMessage = (filter, type) => {
    if (type === 'product') {
        switch (filter) {
            case 'all':
                return texts.NO_SUBSCRIPTIONS_FOR_PODUCT;
            case 'transferred':
                return texts.NO_TRANSFERED_SUBSCRIPTIONS_FOR_PRODUCT;
            case 'pending':
                return texts.NO_NON_TRANSFERED_SUBSCRIPTIONS_FOR_PRODUCT;
            default:
                return texts.NO_SUBSCRIPTIONS_FOR_PODUCT;
        }
    } else {
        switch (filter) {
            case 'all':
                return texts.NO_DISTIBUTOR_SUBSCRIPTIONS;
            case 'transferred':
                return texts.NO_DISTIBUTOR_TRANSFERED_SUBSCRIPTIONS;
            case 'pending':
                return texts.NO_DISTIBUTOR_NON_TRANSFERED_SUBSCRIPTIONS;
            default:
                return texts.NO_DISTIBUTOR_SUBSCRIPTIONS;
        }
    }
};

const OngoingSubscriptionAnalytics = props => {
    const { isAdmin, distributors, distributorsLoader, classes } = props;
    const history = useHistory();

    const { data: fundList = [], isFetching: fundsLoader } = useGetInvestmentFundsQuery();

    const [selectedFund, setSelectedFund] = useState('all');
    const [selectedDistributor, setSelectedDistributor] = useState('all');
    const [subFilter1, setSubFilter1] = useState('all');
    const [subFilter2, setSubFilter2] = useState('all');

    const { data: dataByFund = [], isFetching: dataByFundLoading } =
        useGetDistributorSubscribersDataQuery({
            subscriptionStatus: subscriptionStatus.IN_PROGRESS,
        });
    const { data: dataByDistributor = [], isFetching: dataByDistributorLoading } =
        useGetDistributorSubscribersDataQuery({
            cifId: selectedDistributor === 'all' ? undefined : selectedDistributor,
            subscriptionStatus: subscriptionStatus.IN_PROGRESS,
        });

    // used for charts that show data by fund
    const filteredFundList1 = useMemo(
        () => fundList.filter(f => f.visible && !!dataByFund?.[f.id]),
        [fundList, dataByFund]
    );

    // used for charts that show data by distributors
    const filteredFundList2 = useMemo(
        () => fundList.filter(f => f.visible && !!dataByDistributor?.[f.id]),
        [fundList, dataByDistributor]
    );

    const valueByFundComputationKey = useMemo(() => getSumKeyForFilter(subFilter1), [subFilter1]);
    const numberByFundComputationKey = useMemo(
        () => getNumberKeyForFilter(subFilter1),
        [subFilter1]
    );
    const valueByDistributorComputationKey = useMemo(
        () => getSumKeyForFilter(subFilter2),
        [subFilter2]
    );
    const numberByDistributorComputationKey = useMemo(
        () => getNumberKeyForFilter(subFilter2),
        [subFilter2]
    );

    const totalValueByFund = useMemo(
        () => customTotalParser(dataByFund, selectedFund, valueByFundComputationKey),
        [dataByFund, selectedFund, valueByFundComputationKey]
    );
    const totalNumberByFund = useMemo(
        () => customTotalParser(dataByFund, selectedFund, numberByFundComputationKey),
        [dataByFund, selectedFund, numberByFundComputationKey]
    );
    const totalValueByDistributor = useMemo(
        () => customTotalParser(dataByDistributor, 'all', valueByDistributorComputationKey),
        [dataByDistributor, valueByDistributorComputationKey]
    );
    const totalNumberByDistributor = useMemo(
        () => customTotalParser(dataByDistributor, 'all', numberByDistributorComputationKey),
        [dataByDistributor, numberByDistributorComputationKey]
    );

    const handleInvestmentsByProduct = useCallback(() => {
        history.push({
            pathname: '/subscriptions',
            search: `?${qs.stringify(
                {
                    product: selectedFund === 'all' ? undefined : [selectedFund],
                    pendingTransfer: subFilter1 === 'pending' || undefined,
                    transferred: subFilter1 === 'transferred' || undefined,
                },
                queryStringifyOptions
            )}`,
        });
    }, [history, selectedFund, subFilter1]);

    const handleInvestmentsByDistributor = useCallback(() => {
        history.push({
            pathname: '/subscriptions',
            search: `?${qs.stringify(
                {
                    dist: selectedDistributor === 'all' ? undefined : [selectedDistributor],
                    pendingTransfer: subFilter2 === 'pending' || undefined,
                    transferred: subFilter2 === 'transferred' || undefined,
                },
                queryStringifyOptions
            )}`,
        });
    }, [history, selectedDistributor, subFilter2]);

    return (
        <DashboardSection title={texts.ONGOING}>
            <Loader loading={fundsLoader || distributorsLoader} marginTop={10}>
                <Box mb={6}>
                    <Typography variant="subtitle1" gutterBottom>
                        <b>{texts.SUBSCRIPTIONS_BY_PRODUCT}</b>
                    </Typography>
                    <Box display="flex" alignItems="center">
                        <CustomSelect
                            inputStyle={classes.filterInput}
                            value={selectedFund}
                            onChange={e => setSelectedFund(e.target.value)}
                        >
                            <MenuItem value="all">{texts.ALL_1}</MenuItem>
                            {filteredFundList1.map(f => (
                                <MenuItem key={f.id} value={f.id}>
                                    {f.name}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                        <CustomSelect
                            value={subFilter1}
                            onChange={e => setSubFilter1(e.target.value)}
                            inputStyle={classes.filterInput}
                        >
                            <MenuItem value="all">{texts.ALL_2}</MenuItem>
                            <MenuItem value="transferred">{texts.TRANSFERED}</MenuItem>
                            <MenuItem value="pending">{texts.NON_TRANSFERED}</MenuItem>
                        </CustomSelect>
                    </Box>
                    <Loader loading={dataByFundLoading} marginTop={0} height="350px">
                        {!totalValueByFund && !totalNumberByFund && (
                            <Typography variant="subtitle2" gutterBottom>
                                {getNoDataMessage(subFilter1, 'product')}
                            </Typography>
                        )}
                        {(!!totalValueByFund || !!totalNumberByFund) && (
                            <Box height="350px" display="flex" alignItems="center">
                                <ChartContainer
                                    useSymbol
                                    title={texts.VALUE}
                                    number={totalValueByFund}
                                    handleClick={handleInvestmentsByProduct}
                                    subtitle={getChartSubtitle(selectedFund, subFilter1, 'VALUE')}
                                >
                                    <Chart
                                        type="pie"
                                        series={parseCustomData(
                                            dataByFund,
                                            selectedFund,
                                            valueByFundComputationKey
                                        )}
                                        options={{
                                            ...pieChartOptions,
                                            tooltip: {
                                                ...pieChartOptions.tooltip,
                                                y: { formatter: val => parseSum(val) },
                                            },
                                            labels:
                                                selectedFund === 'all'
                                                    ? filteredFundList1.map(f => f.name)
                                                    : Object.keys(dataByFund?.[selectedFund] || {}),
                                        }}
                                    />
                                </ChartContainer>
                                <ChartContainer
                                    title={texts.NUMBER}
                                    number={totalNumberByFund}
                                    handleClick={handleInvestmentsByProduct}
                                    subtitle={getChartSubtitle(selectedFund, subFilter1, 'NUMBER')}
                                >
                                    <Chart
                                        type="pie"
                                        series={parseCustomData(
                                            dataByFund,
                                            selectedFund,
                                            numberByFundComputationKey
                                        )}
                                        options={{
                                            ...pieChartOptions,
                                            tooltip: {
                                                ...pieChartOptions.tooltip,
                                                y: {
                                                    formatter: subscriptionNumberFormatter,
                                                },
                                            },
                                            labels:
                                                selectedFund === 'all'
                                                    ? filteredFundList1.map(f => f.name)
                                                    : Object.keys(dataByFund?.[selectedFund] || {}),
                                        }}
                                    />
                                </ChartContainer>
                            </Box>
                        )}
                    </Loader>
                </Box>
                {isAdmin && (
                    <Box>
                        <Typography variant="subtitle1" gutterBottom>
                            <b>{texts.SUBSCRIPTIONS_BY_COMMERCIAL}</b>
                        </Typography>
                        <Loader loading={dataByDistributorLoading} marginTop={0} height="350px">
                            <Box display="flex" alignItems="center">
                                <CustomSelect
                                    inputStyle={classes.filterInput}
                                    value={selectedDistributor}
                                    onChange={e => setSelectedDistributor(e.target.value)}
                                >
                                    <MenuItem value="all">{texts.ALL_1}</MenuItem>
                                    {distributors.map(d => (
                                        <MenuItem key={d.id} value={d.user.id}>
                                            {parseFullName(d.user)}
                                        </MenuItem>
                                    ))}
                                </CustomSelect>
                                <CustomSelect
                                    value={subFilter2}
                                    onChange={e => setSubFilter2(e.target.value)}
                                    inputStyle={classes.filterInput}
                                >
                                    <MenuItem value="all">{texts.ALL_2}</MenuItem>
                                    <MenuItem value="transferred">{texts.TRANSFERED}</MenuItem>
                                    <MenuItem value="pending">{texts.NON_TRANSFERED}</MenuItem>
                                </CustomSelect>
                            </Box>
                            {!totalValueByDistributor && !totalNumberByDistributor && (
                                <Typography variant="subtitle2" gutterBottom>
                                    {getNoDataMessage(subFilter2, 'distributor')}
                                </Typography>
                            )}
                            {!!totalValueByDistributor && !!totalNumberByDistributor && (
                                <Box height="350px" display="flex" alignItems="center">
                                    <ChartContainer
                                        useSymbol
                                        title={texts.VALUE}
                                        number={totalValueByDistributor}
                                        handleClick={handleInvestmentsByDistributor}
                                        subtitle={
                                            totalValueByDistributor
                                                ? getChartSubtitle('all', subFilter2, 'VALUE')
                                                : ''
                                        }
                                    >
                                        <Chart
                                            type="pie"
                                            series={parseCustomData(
                                                dataByDistributor,
                                                'all',
                                                valueByDistributorComputationKey
                                            )}
                                            options={{
                                                ...pieChartOptions,
                                                tooltip: {
                                                    ...pieChartOptions.tooltip,
                                                    y: { formatter: val => parseSum(val) },
                                                },
                                                labels: filteredFundList2.map(f => f.name),
                                            }}
                                        />
                                    </ChartContainer>
                                    <ChartContainer
                                        title={texts.NUMBER}
                                        number={totalNumberByDistributor}
                                        handleClick={handleInvestmentsByDistributor}
                                        subtitle={
                                            totalNumberByDistributor
                                                ? getChartSubtitle('all', subFilter2, 'NUMBER')
                                                : ''
                                        }
                                    >
                                        <Chart
                                            type="pie"
                                            series={parseCustomData(
                                                dataByDistributor,
                                                'all',
                                                numberByDistributorComputationKey
                                            )}
                                            options={{
                                                ...pieChartOptions,
                                                tooltip: {
                                                    ...pieChartOptions.tooltip,
                                                    y: {
                                                        formatter: subscriptionNumberFormatter,
                                                    },
                                                },
                                                labels: filteredFundList2.map(f => f.name),
                                            }}
                                        />
                                    </ChartContainer>
                                </Box>
                            )}
                        </Loader>
                    </Box>
                )}
            </Loader>
        </DashboardSection>
    );
};

export default OngoingSubscriptionAnalytics;
